import {
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    BankAccountTypeDisplay,
    ClassNames,
    Color,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { RedactedBankAccount } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { Modal, ModalService } from '@lsq-frontend/common-ui';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { first, map, startWith, take } from 'rxjs/operators';
import { AddBankAccountFormService } from './add-bank-account-form.service';
import { mapBankAccountsToSelectOptions } from './bank-account-helpers/bank-account-select-options';
import { RemoveBankAccountModalBodyComponent } from './bank-account-modals/remove-bank-account-modal.component';
import { BankAccountsService } from './bank-accounts.service';
import { AddBankAccountControllerComponent } from './bank-account-form-controller.component';
import { BankAccountFormControllerService } from './bank-account-form-controller.service';
import { AddIntlBankAccountFormService } from './add-intl-bank-account-form.service';
import { AddBankAccountFormComponent } from './add-bank-account-form.component';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@Component({
    selector: 'supply-chain-bank-accounts',
    templateUrl: './bank-accounts.component.html',
    styleUrls: ['./bank-accounts.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BankAccountsComponent implements OnInit, OnDestroy {
    @ViewChild('removeBankAccountModalBody', { static: true })
    public removeBankAccountModalBody: TemplateRef<any>;
    @ViewChild('makeDefaultModalBody', { static: true })
    public makeDefaultModalBody: TemplateRef<any>;
    @ViewChild('mobileCardTemplate', { static: true })
    public mobileCardTemplate: TemplateRef<any>;

    public bankAccounts$: Observable<RedactedBankAccount[]>;

    public initialized$ = this._bankAccountsService.bankAccountsListInitialized$;

    public bankAccountWarningMessage$: Observable<string>;
    public showAutomaticFundingSection$: Observable<boolean>;

    public userCanEditBankAccounts$ = combineLatest(
        this.claimService.isOn(DashboardClaim.FinancingEditBankAccounts),
        this.claimService.isOn(DashboardClaim.FinancingPlatformEnrollment)
    ).pipe(
        map(
            ([canEditBankAccounts, platformEnrollment]) => canEditBankAccounts || platformEnrollment
        )
    );

    public hasNoData$ = this._bankAccountsService.bankAccounts$.pipe(
        map((bankAccounts) => !bankAccounts || !bankAccounts.length)
    );

    // Constants
    public icons = Icons;
    public color = Color;
    public tableAppearance = TableAppearance;
    public dashboardClaim = DashboardClaim;

    public dataSource = new LsqListSource<RedactedBankAccount>({
        rows$: this._bankAccountsService.bankAccounts$
    });

    public waitingBankAccountVerificationWarningMessage = this._i18n(
        'We need to verify your default payment method before you can request early payments. This process should take 1-3 business days.'
    );

    public removeBankAccountModalTitle = this._i18n({
        value: 'Remove Payment Method',
        meaning: 'Remove bank account modal header'
    });

    public removeBankAccountModalConfirmation = this._i18n({
        value: 'Confirm and Remove',
        meaning: 'Remove bank account modal confirm button'
    });

    public removeAndSetDefaultBankAccountModalConfirmation = this._i18n({
        value: 'Confirm and Change Default',
        meaning: 'Remove and change default bank account modal confirm button'
    });

    public removeBankAccountModalSuccess = this._i18n({
        value: 'Successfully removed the payment method ',
        meaning: 'Remove bank account success message'
    });

    public removeBankAccountModalError = this._i18n({
        value: 'Something went wrong while trying to remove the payment method ',
        meaning: 'Remove bank account error message'
    });

    public defaultBankAccountModalTitle = this._i18n({
        value: 'Make Default Payment Method',
        meaning: 'Make default bank account modal header'
    });

    public defaultBankAccountModalConfirmation = this._i18n({
        value: 'Make Default',
        meaning: 'Make default bank account modal confirm button'
    });

    public defaultBankAccountModalSuccess = this._i18n({
        value: 'Successfully updated the default payment method.',
        meaning: 'Make default bank account success message'
    });

    public modalCancelButton = this._i18n({
        value: 'Cancel',
        meaning: 'Modal cancel button'
    });

    public addBankAccountModalTitle = this._i18n({
        value: 'Add Payment Method',
        meaning: 'Add bank account modal title'
    });

    public saveButton = this._i18n({
        value: 'Confirm and Save',
        meaning: 'Add bank account save button'
    });

    public addBankAccountErrorMessage = this._i18n({
        value: `There was a problem creating the payment method.`,
        meaning: 'Add bank account error message'
    });

    constructor(
        public responsiveService: ResponsiveService,
        public claimService: ClaimService,
        public featureFlagService: FeatureFlagService,
        public bankAccountTypeDisplay: BankAccountTypeDisplay,
        private _i18n: I18n,
        private _modalService: ModalService,
        private _alertService: AlertService,
        private _addBankAccountFormService: AddBankAccountFormService,
        private _addIntlBankAccountFormService: AddIntlBankAccountFormService,
        private _bankAccountFormControllerService: BankAccountFormControllerService,
        private _bankAccountsService: BankAccountsService,
        private _featureFlagService: FeatureFlagService
    ) {}

    public async ngOnInit(): Promise<void> {
        await this._bankAccountsService.listBankAccounts();
        this.bankAccounts$ = this._bankAccountsService.bankAccounts$;

        this.bankAccountWarningMessage$ = combineLatest(
            this._bankAccountsService.bankAccounts$,
            this._bankAccountsService.hasVerifiedBankAccount$
        ).pipe(
            map(([bankAccounts, hasVerifiedBankAccount]) => {
                if (!hasVerifiedBankAccount && bankAccounts.length) {
                    return this.waitingBankAccountVerificationWarningMessage;
                }
                return null;
            })
        );

        this.showAutomaticFundingSection$ = this.claimService.isOn(
            DashboardClaim.FinancingSubmitFundingBatch
        );
    }

    public ngOnDestroy(): void {}

    public async handleAddBankAccountClick(): Promise<void> {
        /// Not using international wires as part of CounselLink.
        if (
            this.featureFlagService.isOnSync(FeatureFlags.InternationalWiresPhase1) &&
            !this.claimService.isOnSync(DashboardClaim.FinancingPlatformEnrollment)
        ) {
            await this._bankAccountFormControllerService.initFormGroup();
        } else {
            this._addBankAccountFormService.initFormGroup();
        }

        const okButtonIsDisabled$ = this._addBankAccountFormService.disableSubmit$.pipe(
            startWith(true)
        );

        const okButtonIsDisabledForIntl$ = combineLatest([
            this._addBankAccountFormService.disableSubmit$,
            this._addIntlBankAccountFormService.disableSubmit$
        ]).pipe(
            map(
                ([bankFormDisabled, intlBankFormDisabled]) =>
                    bankFormDisabled && intlBankFormDisabled
            ),
            startWith(true)
        );

        if (
            this.featureFlagService.isOnSync(FeatureFlags.InternationalWiresPhase1) &&
            !this.claimService.isOnSync(DashboardClaim.FinancingPlatformEnrollment)
        ) {
            this._modalService.showModal(
                new Modal({
                    title: this.addBankAccountModalTitle,
                    bodyComponent: AddBankAccountControllerComponent,
                    okButtonText: this.saveButton,
                    okButtonSize: ClassNames.Block,
                    cancelButtonText: this.modalCancelButton,
                    width: '60rem',
                    onOkClick: async () => {
                        try {
                            await this._bankAccountFormControllerService.submitForm();
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this._bankAccountsService.listBankAccounts();
                        } catch {
                            this._alertService.error(this.addBankAccountErrorMessage);
                        } finally {
                            this._modalService.closeModal();
                        }
                    },
                    onCancelClick: () => this._modalService.closeModal(),
                    isSubmitting$: this._addBankAccountFormService.isSubmitting$,
                    okButtonIsDisabled$: okButtonIsDisabledForIntl$,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: false,
                    clickOutsideToClose: false
                })
            );
        } else {
            this._modalService.showModal(
                new Modal({
                    title: this.addBankAccountModalTitle,
                    bodyComponent: AddBankAccountFormComponent,
                    okButtonText: this.saveButton,
                    okButtonSize: ClassNames.Block,
                    cancelButtonText: this.modalCancelButton,
                    width: '60rem',
                    onOkClick: async () => {
                        try {
                            await this._addBankAccountFormService.submitForm();
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this._bankAccountsService.listBankAccounts();
                        } catch {
                            this._alertService.error(this.addBankAccountErrorMessage);
                        } finally {
                            this._modalService.closeModal();
                        }
                    },
                    onCancelClick: () => this._modalService.closeModal(),
                    isSubmitting$: this._addBankAccountFormService.isSubmitting$,
                    okButtonIsDisabled$,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: false,
                    clickOutsideToClose: false
                })
            );
        }
    }

    public async handleRemoveBankAccountClick(bankAccount: RedactedBankAccount): Promise<void> {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        const verifiedBankAccounts = await this.bankAccounts$
            .pipe(
                map((bankAccounts) => bankAccounts.filter((ba) => ba.isVerified && !ba.isDefault)),
                take(1)
            )
            .toPromise();
        const bankAccountSelectOptions = mapBankAccountsToSelectOptions(
            verifiedBankAccounts,
            this.bankAccountTypeDisplay
        );
        const bankAccountFormControl = new FormControl(verifiedBankAccounts[0]?.bankAccountKey);
        const showButtons =
            !bankAccount.isDefault || (bankAccount.isDefault && verifiedBankAccounts.length > 0);

        this._modalService.showModal(
            new Modal({
                title: this.removeBankAccountModalTitle,
                data: {
                    bankAccount,
                    bankAccountSelectOptions,
                    bankAccountFormControl
                },
                width: '600px',
                bodyComponent: RemoveBankAccountModalBodyComponent,
                okButtonText: bankAccount.isDefault
                    ? this.removeAndSetDefaultBankAccountModalConfirmation
                    : this.removeBankAccountModalConfirmation,
                cancelButtonText: this.modalCancelButton,
                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    try {
                        if (bankAccount.isDefault) {
                            await this._bankAccountsService.updateSellerDefaultBankAccount(
                                bankAccountFormControl.value
                            );
                        }
                        await this._bankAccountsService.removeBankAccount(
                            bankAccount.bankAccountKey
                        );
                        this._alertService.success(
                            `${this.removeBankAccountModalSuccess}${bankAccount.bankName} ` +
                                `(${bankAccount.redactedAccountNumber})`
                        );
                        this._bankAccountsService.bankAccounts$.pipe(first()).subscribe(() => {
                            isSubmittingSubject.next(false);
                            this._modalService.closeModal();
                        });
                    } catch (error) {
                        isSubmittingSubject.next(false);
                        this._alertService.error(
                            `${this.removeBankAccountModalError}` +
                                `${bankAccount.bankName} (${bankAccount.redactedAccountNumber})`
                        );
                        throw error;
                    }
                },
                isSubmitting$: isSubmittingSubject.asObservable(),
                okButtonSize: ClassNames.Block,
                showFooter: showButtons,
                showOkButton: showButtons,
                showCancelButton: showButtons,
                showCloseButton: true
            })
        );
    }

    public handleMakeDefaultBankAccountClick(bankAccount: RedactedBankAccount): void {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const isPlatform = this.claimService.isOnSync(DashboardClaim.FinancingPlatformEnrollment);
        this._modalService.showModal(
            new Modal({
                title: this.defaultBankAccountModalTitle,
                templateRef: this.makeDefaultModalBody,
                context: {
                    accountName: bankAccount.accountName,
                    bankName: bankAccount.bankName,
                    redactedAccountNumber: bankAccount.redactedAccountNumber,
                    isPlatform: isPlatform
                },
                okButtonText: this.defaultBankAccountModalConfirmation,
                cancelButtonText: this.modalCancelButton,
                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    await this._bankAccountsService.updateSellerDefaultBankAccount(
                        bankAccount.bankAccountKey
                    );
                    this._alertService.success(this.defaultBankAccountModalSuccess);
                    this._bankAccountsService.bankAccounts$.pipe(first()).subscribe(() => {
                        isSubmittingSubject.next(false);
                        this._modalService.closeModal();
                    });
                },
                isSubmitting$: isSubmittingSubject.asObservable(),
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                showCloseButton: false,
                clickOutsideToClose: false
            })
        );
    }
}
